import React, {
    useMemo,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react';
import {
    Box,
    Button,
    Container,
    Header,
    SpaceBetween,
    StatusIndicator,
    Alert,
    Popover,
    Icon,
} from '@amzn/awsui-components-react-v3';
import { useDispatch } from 'react-redux';
import {
    updateSelectedActivity,
    getSelectedActivity,
} from '../../../../store/slices/selectedActivitySlice';
import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import {
    useClassroomSetupStatus,
    SetupStep,
    StatusIndicatorType,
} from './useClassroomSetupStatus';
import useClassroomManagement from '../../Common/useClassroomManagement';
import { InfoLink } from '../../../../../common/components/InfoLink/InfoLink';
import { useHelpPanel } from '../../../../../common/context/HelpPanelContext';
import { CLASSROOM_PANEL_CONTENT } from '../../../../../common/components/HelpPanel/HelpPanelContent';
import useClassroomStatusPolling from '../../Common/useClassroomStatusPolling';
import { ActivityModality } from '../../Common/Common';
import './ClassroomStatusPanel.css';

// Polling interval in milliseconds (5 seconds)
const VILT_POLLING_INTERVAL = 5000;

interface ClassroomStatusPanelProps {
    activity: ActivityData;
}

/**
 * Maps our status types to Cloudscape StatusIndicator types
 */
const mapStatusToIndicator = (
    status: StatusIndicatorType,
):
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'stopped'
    | 'pending'
    | 'in-progress'
    | 'loading' => {
    return status;
};

/**
 * Renders an individual setup step with status indicator
 */
const SetupStepItem: React.FC<{ step: SetupStep }> = ({ step }) => {
    return (
        <div
            className="classroom-setup-step"
            data-testid={`setup-step-${step.id}`}
        >
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <StatusIndicator type={mapStatusToIndicator(step.status)}>
                    {step.label}
                </StatusIndicator>
                {step.helpText && (
                    <Popover
                        dismissButton={false}
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            <Box color="text-body-secondary">
                                {step.helpText}
                            </Box>
                        }
                    >
                        <span className="classroom-setup-help-icon">
                            <Icon name="status-info" variant="subtle" />
                        </span>
                    </Popover>
                )}
            </SpaceBetween>
            {step.detail && (
                <Box
                    color="text-status-error"
                    fontSize="body-s"
                    padding={{ left: 'l' }}
                >
                    {step.detail}
                </Box>
            )}
        </div>
    );
};

/**
 * ClassroomStatusPanel displays the classroom setup progress as a checklist,
 * replacing the previous button-driven approach with a status-driven display.
 *
 * Features:
 * - Shows checklist of eligibility requirements with real-time status indicators
 * - Displays WebEx meeting creation progress
 * - Shows classroom creation status
 * - Provides retry button for failed states
 * - Shows actionable error messages
 */
export const ClassroomStatusPanel: React.FC<ClassroomStatusPanelProps> = ({
    activity,
}) => {
    const dispatch = useDispatch();
    const {
        steps,
        overallStatus,
        canRetry,
        errorMessage,
        hasClassroom,
        viltMeetingStatus,
        classroomStatus,
        isEligible,
    } = useClassroomSetupStatus(activity);

    const { handleSubmitEvent, submitting } = useClassroomManagement();

    const [pollAfterSave, setPollAfterSave] = useState(false);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSaveActivity = useCallback(async () => {
        if (pollAfterSave) return;
        await dispatch<any>(
            updateSelectedActivity(
                activity.pk,
                { delivery_sessions: activity.delivery_sessions },
                { triggerLoadingState: false },
            ),
        );
        dispatch<any>(
            getSelectedActivity(activity.pk, { triggerLoadingState: false }),
        );
        setPollAfterSave(true);
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        pollTimerRef.current = setTimeout(() => setPollAfterSave(false), 15000);
    }, [activity.pk, activity.delivery_sessions, dispatch, pollAfterSave]);

    useEffect(() => {
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, []);
    const {
        actions: { makeHelpPanelHandler },
    } = useHelpPanel();

    // Compute if all prerequisites are met for auto-triggering classroom creation
    const shouldAutoTrigger = useMemo(() => {
        const isVirtual =
            activity.activity_modality === ActivityModality.vILT ||
            activity.activity_modality === ActivityModality.Hybrid;

        const webexReady = !isVirtual || viltMeetingStatus.type === 'completed';
        const allPrerequisitesMet = isEligible && webexReady;

        // Classroom creation not yet triggered - includes null/empty AND the "allowed" statuses
        // COURSE_ALLOWED_FOR_CLASSROOM_CREATION means eligible but not yet started
        // ELIGIBLE_FOR_CLASSROOM_CREATION is another pre-start status
        // READY_FOR_CLASSROOM_CREATION means it's already triggered and in progress - do NOT trigger again
        const classroomNotYetTriggered =
            !classroomStatus ||
            classroomStatus === '' ||
            classroomStatus === null ||
            classroomStatus ===
                ClassroomStatus.COURSE_ALLOWED_FOR_CLASSROOM_CREATION ||
            classroomStatus === ClassroomStatus.ELIGIBLE_FOR_CLASSROOM_CREATION;

        // If already in READY_FOR_CLASSROOM_CREATION, don't trigger - it's already in progress
        const classroomCreationInProgress =
            classroomStatus === ClassroomStatus.READY_FOR_CLASSROOM_CREATION;

        const notInErrorState =
            classroomStatus !== ClassroomStatus.CLASSROOM_UPDATE_FAILED &&
            classroomStatus !== ClassroomStatus.CLASSROOM_PROVISIONING_FAILED;

        const shouldTrigger =
            allPrerequisitesMet &&
            classroomNotYetTriggered &&
            !classroomCreationInProgress &&
            notInErrorState &&
            !hasClassroom;

        return shouldTrigger;
    }, [
        isEligible,
        viltMeetingStatus.type,
        classroomStatus,
        hasClassroom,
        activity.activity_modality,
    ]);

    // Store the submit handler in a ref to avoid effect re-running due to handler changes
    const handleSubmitRef = useRef(handleSubmitEvent);
    handleSubmitRef.current = handleSubmitEvent;

    // Track if we've already triggered classroom creation for this activity
    const triggeredForActivityRef = useRef<string | null>(null);

    // Auto-trigger classroom creation when all prerequisites are met
    useEffect(() => {
        // Only trigger if:
        // 1. All prerequisites are met
        // 2. Not currently submitting
        // 3. We haven't triggered for this specific activity yet
        const activityId = activity?.pk;

        if (
            shouldAutoTrigger &&
            !submitting &&
            activityId &&
            triggeredForActivityRef.current !== activityId
        ) {
            triggeredForActivityRef.current = activityId;
            handleSubmitRef.current();
        }

        // Reset the trigger flag if classroom is ready (allow future re-triggers if needed)
        if (hasClassroom) {
            triggeredForActivityRef.current = null;
        }
    }, [shouldAutoTrigger, submitting, activity?.pk, hasClassroom]);

    // vILT polling: polls for WebEx meeting creation/update status changes
    // Applies to: Hold, Active, Tentative activities with WebEx v_ilt_type
    const shouldPollVilt = useMemo(() => {
        const isViltOrHybrid =
            activity.activity_modality === ActivityModality.vILT ||
            activity.activity_modality === ActivityModality.Hybrid;
        const usesAutoCreatedVilt =
            activity.v_ilt_type && activity.v_ilt_type !== 'Other';
        const viltEligibleStatus = ['Hold', 'Active', 'Tentative'].includes(
            activity.activity_status,
        );

        if (!isViltOrHybrid || !usesAutoCreatedVilt || !viltEligibleStatus) {
            return false;
        }

        if (viltMeetingStatus.type === 'processing') {
            return true;
        }

        if (viltMeetingStatus.type === 'completed' && !activity.v_ilt_id) {
            return true;
        }

        if (!activity.v_ilt_meeting_creation_status) {
            return true;
        }

        if (pollAfterSave) {
            return true;
        }

        return false;
    }, [
        activity.activity_modality,
        activity.activity_status,
        activity.v_ilt_type,
        activity.v_ilt_id,
        activity.v_ilt_meeting_creation_status,
        pollAfterSave,
        viltMeetingStatus.type,
    ]);

    // Classroom polling: polls for classroom creation progress
    // Only applies to Active activities
    const shouldPollClassroom = useMemo(() => {
        if (activity.activity_status !== 'Active') {
            return false;
        }
        if (hasClassroom) {
            return false;
        }
        return overallStatus === 'in-progress' || overallStatus === 'eligible';
    }, [activity.activity_status, hasClassroom, overallStatus]);

    const shouldPoll = shouldPollVilt || shouldPollClassroom;

    // Use polling hook to refresh activity data when waiting for updates
    useClassroomStatusPolling(
        shouldPoll,
        activity,
        dispatch,
        VILT_POLLING_INTERVAL,
    );

    const getHeaderDescription = (): string => {
        switch (overallStatus) {
            case 'completed':
                return 'Classroom is ready for use';
            case 'in-progress':
                return 'Setting up classroom...';
            case 'error':
                return 'Complete the action below to resolve the issue';
            case 'eligible':
                return 'All requirements met - classroom will be created automatically';
            case 'not-started':
            default:
                return 'Complete the requirements below to enable classroom creation';
        }
    };

    const renderRetryButton = () => {
        if (!canRetry) return null;

        return (
            <Button
                data-testid="retry-classroom-button"
                onClick={handleSubmitEvent}
                loading={submitting}
                iconName="refresh"
            >
                Retry
            </Button>
        );
    };

    const renderHeaderActions = () => {
        return (
            <SpaceBetween direction="horizontal" size="xs">
                {renderRetryButton()}
            </SpaceBetween>
        );
    };

    return (
        <Container
            data-testid="classroom-status-panel"
            header={
                <Header
                    variant="h2"
                    description={getHeaderDescription()}
                    actions={renderHeaderActions()}
                    info={
                        <InfoLink
                            onFollow={makeHelpPanelHandler(
                                CLASSROOM_PANEL_CONTENT.title,
                            )}
                        />
                    }
                >
                    Classroom Setup
                </Header>
            }
        >
            <SpaceBetween size="s">
                {/* Error Alert - only show for actionable errors */}
                {errorMessage && (
                    <Alert
                        type={
                            viltMeetingStatus.type === 'warning'
                                ? 'warning'
                                : 'error'
                        }
                        header={
                            viltMeetingStatus.type === 'warning'
                                ? 'Instructor WebEx license required'
                                : viltMeetingStatus.type === 'error'
                                  ? 'WebEx meeting setup failed'
                                  : 'Classroom setup failed'
                        }
                        data-testid="classroom-error-alert"
                        action={
                            viltMeetingStatus.type === 'error' ||
                            viltMeetingStatus.type === 'warning' ? (
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        href="https://issues.amazon.com/issues/create?template=91e38ad6-6a3a-42c9-a847-b204f480a1fe"
                                        target="_blank"
                                        iconAlign="right"
                                        iconName="external"
                                        variant="normal"
                                    >
                                        Request a WebEx license
                                    </Button>
                                    {viltMeetingStatus.type === 'warning' && (
                                        <Button
                                            onClick={handleSaveActivity}
                                            loading={pollAfterSave}
                                        >
                                            Retry
                                        </Button>
                                    )}
                                </SpaceBetween>
                            ) : canRetry ? (
                                <Button
                                    onClick={handleSubmitEvent}
                                    loading={submitting}
                                >
                                    Retry
                                </Button>
                            ) : undefined
                        }
                    >
                        {errorMessage}
                    </Alert>
                )}

                {/* Setup Steps Checklist - this is the primary status display */}
                <div className="classroom-setup-steps">
                    {steps.map((step) => (
                        <SetupStepItem key={step.id} step={step} />
                    ))}
                </div>

                {/* Additional info based on status - only show one message */}
                {shouldPoll && !hasClassroom && (
                    <Box color="text-body-secondary" fontSize="body-s">
                        <Icon name="status-in-progress" /> This process
                        typically takes 1-2 minutes. The page will update
                        automatically.
                    </Box>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default ClassroomStatusPanel;
